// src/features/dashboard/accesos/Accesos.jsx
"use client";

import React from "react";
import { Badge, Box, Card, Combobox, createListCollection, Dialog, Field, Fieldset, Flex, HStack, Input, NativeSelect, Portal, SimpleGrid, Spinner, Text } from "@chakra-ui/react";
import { MdDeleteOutline, MdOutlineBadge, MdOutlineCheckCircle, MdOutlineLayers, MdOutlinePeople, MdOutlinePersonOff, MdOutlineSearch, MdOutlineSupervisorAccount } from "react-icons/md";
import { Button } from "components/ui/Button";
import AppAvatar from "components/utils/AppAvatar";
import { TagCombobox } from "components/utils/TagCombobox";
import StatCard from "components/utils/StatCard";
import { useDashboardSettings } from "features/dashboard/settings/store";
import { useDashboardAccesos, useSetDashboardAccesosForUsuario, useClearDashboardAccesosForUsuario, useSearchDashboardInstructores } from "features/dashboard/accesos/hooks";
import { resolveAvatarUrl } from "utils/avatar";

const toStr = (v) => (v == null ? "" : String(v));
const toNull = (v) => {
  const s = String(v ?? "").trim();
  return s.length ? s : null;
};

const AccesoCard = ({ correo, rol, instructorNombre, nivelCount, hasAcceso, avatarSrc, avatarSeed, onClick }) => {
  return (
    <Card.Root
      flexDirection="row"
      overflow="hidden"
      cursor="pointer"
      bg="dashboard.bg.surface"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      rounded="card"
      transition="transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease"
      _hover={{ transform: "translateY(-2px)", boxShadow: "cardLift", borderColor: "dashboard.border.default" }}
      onClick={onClick}
    >
      <Box p="4" display="flex" alignItems="center" justifyContent="center">
        <AppAvatar size="2xl" name={instructorNombre || correo} src={avatarSrc || undefined} seed={avatarSeed || correo} />
      </Box>
      <Card.Body>
        <Card.Title mb="1" fontFamily="display" color="dashboard.fg.default" lineClamp={1}>
          {correo}
        </Card.Title>
        <Card.Description>
          <HStack gap="2" wrap="wrap">
            <Badge colorPalette={hasAcceso ? "green" : "red"} variant="subtle" rounded="full">{hasAcceso ? "Con acceso" : "Sin acceso"}</Badge>
            <Badge variant="subtle" rounded="full">{rol || "-"}</Badge>
            <Badge variant="subtle" rounded="full">Niveles: {nivelCount}</Badge>
            <Badge variant="subtle" rounded="full">{instructorNombre ? `Instructor: ${instructorNombre}` : "Sin instructor"}</Badge>
          </HStack>
        </Card.Description>
      </Card.Body>
    </Card.Root>
  );
};

const InstructorCombobox = ({ value, label, onChange, disabled }) => {
  const [query, setQuery] = React.useState(label || "");
  const q = useSearchDashboardInstructores(query, !disabled);

  const items = React.useMemo(() => {
    const rows = Array.isArray(q.data) ? q.data : [];
    return rows.map((r) => ({
      label: r.nombre,
      value: r.id,
      avatar: resolveAvatarUrl(r),
    }));
  }, [q.data]);

  const collection = React.useMemo(() => createListCollection({ items }), [items]);

  React.useEffect(() => {
    setQuery(label || "");
  }, [label]);

  return (
    <Combobox.Root
      collection={collection}
      value={value ? [value] : []}
      onValueChange={(e) => {
        const id = e.value?.[0] || "";
        const picked = items.find((x) => x.value === id);
        onChange(
          picked
            ? { id: picked.value, nombre: picked.label, fotoUrl: picked.avatar || null }
            : { id: null, nombre: "", fotoUrl: null }
        );
      }}
      onInputValueChange={(e) => setQuery(e.inputValue || "")}
      disabled={disabled}
      openOnClick
    >
      <Combobox.Label>Instructor (opcional)</Combobox.Label>

      <Combobox.Control bg="dashboard.bg.input" color="dashboard.fg.default" borderColor="dashboard.border.default">
        <Combobox.Input placeholder="Busca por nombre (min 2 letras)" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Combobox.Trigger />
      </Combobox.Control>

      <Portal>
        <Combobox.Positioner style={{ zIndex: 2000 }}>
          <Combobox.Content bg="dashboard.bg.popover" color="dashboard.fg.default" borderColor="dashboard.border.default">
            {items.length === 0 ? (
              <Box p="3" opacity="0.7">
                <Text>Sin resultados</Text>
              </Box>
            ) : (
              items.map((item) => (
                <Combobox.Item key={item.value} item={item} _highlighted={{ bg: "dashboard.bg.surfaceMuted" }}>
                  <HStack gap="2">
                    <AppAvatar size="2xs" name={item.label} src={item.avatar} seed={item.value || item.label} />
                    <Combobox.ItemText>{item.label}</Combobox.ItemText>
                  </HStack>
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
};

const Accesos = () => {
  const s = useDashboardSettings();
  const q = useDashboardAccesos(!!s.ready);

  const setM = useSetDashboardAccesosForUsuario();
  const clearM = useClearDashboardAccesosForUsuario();

  const [search, setSearch] = React.useState("");


  const rows = React.useMemo(() => {
    const raw = Array.isArray(q.data?.rows) ? q.data.rows : (Array.isArray(q.data) ? q.data : []);
    const needle = String(search || "").trim().toLowerCase();
    const filtered = needle.length ? raw.filter((r) => String(r.correo || "-").toLowerCase().includes(needle)) : raw;

    return filtered.map((r, i) => {
      const accesos = Array.isArray(r.accesos) ? r.accesos : [];
      const nivelIds = accesos.map((a) => a.nivelId);
      const instructorFotoUrl = r.instructorFotoUrl ?? r.fotoUrl ?? null;

      return {
        usuarioId: r.usuarioId,
        correo: r.correo || "-",
        instructorId: r.instructorId ?? null,
        instructorNombre: r.instructorNombre ?? "",
        instructorFotoUrl,
        rol: accesos[0]?.rol ?? "",
        nivelIds,
        nivelCount: nivelIds.length,
        hasAcceso: nivelIds.length > 0,
        avatarSeed: r.instructorId || r.correo || i,
        raw: r,
      };
    });
  }, [q.data, search]);


  const totalUsuarios = rows.length;
  const totalConAcceso = rows.filter((x) => x.hasAcceso).length;
  const totalSinAcceso = totalUsuarios - totalConAcceso;
  const totalNiveles = rows.reduce((sum, row) => sum + row.nivelCount, 0);

  const [open, setOpen] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const [editingUsuarioId, setEditingUsuarioId] = React.useState(null);
  const [fCorreo, setFCorreo] = React.useState("");
  const [fRol, setFRol] = React.useState("jefe");
  const [fNivelIds, setFNivelIds] = React.useState([]);
  const [fInstructorId, setFInstructorId] = React.useState(null);
  const [fInstructorNombre, setFInstructorNombre] = React.useState("");
  const [fInstructorFoto, setFInstructorFoto] = React.useState(null);

  const allNiveles = s.options?.niveles || [];

  // mapa id -> nombre (para mostrar tags bonito)
  const nivelLabelById = React.useMemo(() => {
    const m = new Map();
    for (const n of allNiveles) m.set(n.id, n.nombre);
    return m;
  }, [allNiveles]);

  // ✅ niveles asignables = lo que ya te dio el contexto
  const nivelItems = React.useMemo(
    () => allNiveles.map((n) => ({ label: n.nombre, value: n.id })),
    [allNiveles]
  );

  // set de ids válidos (para evitar mandar "6to" o basura)
  const knownNivelIds = React.useMemo(
    () => new Set(allNiveles.map((n) => n.id)),
    [allNiveles]
  );

  // para que si ya trae niveles que no están en items (caso raro), al menos se vean con label
  const nivelItemsForUi = React.useMemo(() => {
    const m = new Map(nivelItems.map((x) => [x.value, x]));
    for (const id of fNivelIds) {
      if (!m.has(id)) m.set(id, { value: id, label: nivelLabelById.get(id) || id });
    }
    return Array.from(m.values());
  }, [nivelItems, fNivelIds, nivelLabelById]);

  const saving = setM.isPending;
  const deleting = clearM.isPending;
  const error = setM.error || clearM.error;

  const openForUser = (item) => {
    setConfirmDelete(false);
    setEditingUsuarioId(item.usuarioId);
    setFCorreo(item.correo);
    setFRol(toStr(item.rol) || "jefe");
    setFNivelIds(Array.isArray(item.nivelIds) ? item.nivelIds : []);
    setFInstructorId(item.instructorId ?? null);
    setFInstructorNombre(item.instructorNombre ?? "");
    setFInstructorFoto(item.instructorFotoUrl ?? null);
    setOpen(true);
  };

  const onSave = async () => {
    const rol = toNull(fRol) || "jefe";
    if (!editingUsuarioId) return;

    // ✅ NO dejes pasar strings tipo "6to" si no son ids reales
    const cleanNivelIds = Array.from(
      new Set((fNivelIds || []).filter((id) => knownNivelIds.has(id)))
    );

    await setM.mutateAsync({
      usuarioId: editingUsuarioId,
      payload: { nivelIds: cleanNivelIds, rol, instructorId: fInstructorId ?? null },
    });

    setOpen(false);
  };


  const onDeleteAll = async () => {
    if (!editingUsuarioId) return;
    await clearM.mutateAsync(editingUsuarioId);
    setOpen(false);
  };

  if (!s.ready) {
    return (
      <Box py="10" display="flex" alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="4"
        mb="6"
      >
        <Box>
          <Text
            fontFamily="display"
            fontWeight="700"
            fontSize={{ base: "2xl", md: "3xl" }}
            color="dashboard.fg.default"
            lineHeight="1.1"
          >
            Accesos
          </Text>
          <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">Asigna multiples niveles por usuario y vincula instructores.</Text>
        </Box>
      </Flex>

      {q.isError ? (
        <pre style={{ whiteSpace: "pre-wrap", color: "crimson", marginBottom: 12 }}>
          {String(q.error?.message || q.error)}
        </pre>
      ) : null}

      <SimpleGrid columns={[2, 4]} gap="3" pb="6">
        <StatCard title="Usuarios" data={String(totalUsuarios)} icon={MdOutlinePeople} tone="info" />
        <StatCard title="Con acceso" data={String(totalConAcceso)} icon={MdOutlineCheckCircle} tone="success" />
        <StatCard title="Sin acceso" data={String(totalSinAcceso)} icon={MdOutlinePersonOff} tone="danger" />
        <StatCard title="Niveles asignados" data={String(totalNiveles)} icon={MdOutlineLayers} tone="warning" />
      </SimpleGrid>

      <Flex mb="4" gap="3" align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }}>
        <HStack
          flex="1"
          bg="dashboard.bg.surface"
          borderWidth="1px"
          borderColor="dashboard.border.default"
          rounded="card"
          px="3"
          py="2"
        >
          <MdOutlineSearch />
          <Input border="0" outline="0" bg="transparent" placeholder="Buscar por correo..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </HStack>

        <HStack justify={{ base: "flex-start", md: "flex-end" }} wrap="wrap">
          <Badge colorPalette="gray" variant="subtle" rounded="full">Mostrando: {rows.length}</Badge>
          <Badge colorPalette="green" variant="subtle" rounded="full">Con acceso: {totalConAcceso}</Badge>
        </HStack>
      </Flex>

      {q.isLoading ? (
        <Box py="10" display="flex" alignItems="center" justifyContent="center">
          <Spinner />
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="4">
          {rows.map((item) => (
            <AccesoCard
              key={item.usuarioId}
              correo={item.correo}
              rol={item.rol}
              instructorNombre={item.instructorNombre}
              avatarSrc={item.instructorFotoUrl}
              nivelCount={item.nivelCount}
              hasAcceso={item.hasAcceso}
              avatarSeed={item.avatarSeed}
              onClick={() => openForUser(item)}
            />
          ))}
        </SimpleGrid>
      )}

      <Dialog.Root
        open={open}
        onOpenChange={(e) => {
          setOpen(e.open);
          if (!e.open) setConfirmDelete(false);
        }}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="lg" bg="dashboard.bg.dialog" color="dashboard.fg.default" borderWidth="1px" borderColor="dashboard.border.default">
            <Dialog.Header>
              <Dialog.Title>Editar accesos</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <Fieldset.Root size="lg">
                <Fieldset.Content>
                  <Field.Root>
                    <Field.Label>Correo</Field.Label>
                    <Input value={fCorreo} isDisabled />
                  </Field.Root>

                  <Box>
                    <TagCombobox
                      label="Niveles"
                      placeholder="Agrega niveles..."
                      items={nivelItemsForUi}
                      value={fNivelIds}
                      onChange={setFNivelIds}
                      allowCustomValue={false}
                      showAvatar={false}
                    />
                    <Text fontSize="sm" opacity="0.7" mt="1">
                      Puedes asignar multiples niveles en un solo input.
                    </Text>
                  </Box>

                  <Field.Root>
                    <Field.Label>Rol</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field value={fRol} onChange={(e) => setFRol(e.target.value)}>
                        <option value="jefe">jefe</option>
                        <option value="finanzas">finanzas</option>
                        <option value="coordinador">coordinador</option>
                        <option value="staff">staff</option>
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  </Field.Root>

                  <Box>
                    <InstructorCombobox
                      value={fInstructorId}
                      label={fInstructorNombre}
                      onChange={(x) => {
                        setFInstructorId(x.id);
                        setFInstructorNombre(x.nombre || "");
                        setFInstructorFoto(x.fotoUrl ?? null);
                      }}
                    />

                    <HStack mt="2" justifyContent="space-between">
                      <HStack opacity="0.8">
                        <AppAvatar size="xs" name={fInstructorNombre || fCorreo} src={fInstructorFoto || undefined} seed={fInstructorId || fInstructorNombre || fCorreo} />
                        <MdOutlineSupervisorAccount />
                        <Text fontSize="sm">{fInstructorId ? `Vinculado: ${fInstructorNombre}` : "Sin instructor vinculado"}</Text>
                      </HStack>

                      <Button variant="outline" color="red" onClick={() => { setFInstructorId(null); setFInstructorNombre(""); setFInstructorFoto(null); }} disabled={saving || deleting}>
                        <MdOutlineBadge />
                        <Text>Quitar instructor</Text>
                      </Button>
                    </HStack>
                  </Box>

                  {error ? (
                    <Text color="dashboard.feedback.error" whiteSpace="pre-wrap">
                      {String(error?.message || error)}
                    </Text>
                  ) : null}
                </Fieldset.Content>
              </Fieldset.Root>
            </Dialog.Body>

            <Dialog.Footer>
              <HStack justifyContent="space-between" w="full">
                <Box>
                  {confirmDelete ? (
                    <HStack>
                      <Button variant="outline" color="red" onClick={() => setConfirmDelete(false)} disabled={saving || deleting}>
                        Cancelar
                      </Button>
                      <Button color="danger" variant="solid" onClick={onDeleteAll} disabled={saving || deleting}>
                        {deleting ? <Spinner size="sm" /> : null}
                        <Text>Quitar todos los accesos</Text>
                      </Button>
                    </HStack>
                  ) : (
                    <Button variant="outline" color="danger" onClick={() => setConfirmDelete(true)} disabled={saving || deleting}>
                      <MdDeleteOutline />
                      <Text>Eliminar</Text>
                    </Button>
                  )}
                </Box>

                <HStack>
                  <Button variant="outline" color="red" onClick={() => setOpen(false)} disabled={saving || deleting}>
                    Cancelar
                  </Button>
                  <Button color="red" onClick={onSave} disabled={saving || deleting}>
                    {saving ? <Spinner size="sm" /> : null}
                    <Text>Guardar</Text>
                  </Button>
                </HStack>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger />
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default Accesos;

